import React, { useEffect, useState } from 'react';
import { Calendar, Badge, Modal, List, Tag, Spin, Space } from 'antd';
import dayjs from 'dayjs';
import axiosInstance from '@/lib/axiosInstance';
import { useToast } from '@/hooks/use-toast';
import useDocumentTitle from '@/components/Helmet';
import { firstPanelDate, formatYMD, statusLabels } from '@/lib/utils';
import { Button, Menu, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export default function TaskCalendar() {
    useDocumentTitle("Task Calendar");
    const { toast } = useToast();
    const [panelValue, setPanelValue] = useState(() => dayjs()); // current month
    const [stats, setStats] = useState<any>({}); // { 'YYYY-MM-DD': { scheduled, in_progress, completed, cancelled } }
    const [loadingStats, setLoadingStats] = useState(false);

    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [tasks, setTasks] = useState<any>([]);
    const [loadingTasks, setLoadingTasks] = useState(false);

    const [statusPop, setStatusPop] = React.useState<HTMLButtonElement | null>(null);

    const handleStatusPop = (event: React.MouseEvent<HTMLButtonElement>) => {
        setStatusPop(event.currentTarget);
    };

    const handleClose = () => {
        setStatusPop(null);
    };

    const openStatus = Boolean(statusPop);
    const id = open ? 'simple-popover' : undefined;

    // Fetch stats for visible month
    async function fetchStatsForMonth(dj: any) {
        const { start, end } = firstPanelDate(dj);
        const qs = new URLSearchParams({
            start: start.format('YYYY-MM-DD'),
            end: end.format('YYYY-MM-DD'),
        }).toString();

        setLoadingStats(true);
        try {
            const { data } = await axiosInstance(`/api/tasks/stats?${qs}`);

            setStats(data || {});
        }
        catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            });
        } finally {
            setLoadingStats(false);
        }
    }

    useEffect(() => {
        fetchStatsForMonth(panelValue);
    }, [panelValue?.format('YYYY-MM')]); // refetch when month changes

    async function openDayTasks(dj: any) {
        setSelectedDate(dj);
        setOpen(true);
        setLoadingTasks(true);
        try {
            const qs = new URLSearchParams({
                date: formatYMD(dj),
            }).toString();
            const { data } = await axiosInstance(`/api/tasks/by-day?${qs}`);

            setTasks(Array.isArray(data) ? data : []);
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            });
        } finally {
            setLoadingTasks(false);
        }
    }

    const cellRender = (date: any, info: any) => {
        if (info.type !== 'date') return info.originNode;

        const key = formatYMD(date);
        const counts = stats[key];
        // if (!counts) return info.originNode;
        if (!counts) return "";

        const sum = (counts.scheduled || 0) + (counts.in_progress || 0) + (counts.completed || 0) + (counts.cancelled || 0);

        // Minimal, dense indicators with a clickable container
        return (
            <div
                onClick={(e) => { e.stopPropagation(); openDayTasks(date); }}
                style={{ cursor: 'pointer', paddingTop: 2 }}
            >
                {/* <div style={{ fontSize: 12, fontWeight: 600 }}>{date.date()}</div> */}
                <Space size={2} wrap>
                    {counts.scheduled > 0 && (
                        <Badge count={counts.scheduled} color="#1677ff" title="scheduled" />
                    )}
                    {counts.in_progress > 0 && (
                        <Badge count={counts.in_progress} color="#faad14" title="in_progress" />
                    )}
                    {counts.completed > 0 && (
                        <Badge count={counts.completed} color="#52c41a" title="completed" />
                    )}
                    {counts.cancelled > 0 && (
                        <Badge count={counts.cancelled} color="#ff4d4f" title="cancelled" />
                    )}
                    {sum === 0 && <span style={{ fontSize: 10, color: '#999' }}>—</span>}
                </Space>
            </div>
        );
    };

    return (
        <>
            <div style={{ marginBottom: 8 }}>
                {loadingStats ? <Spin size="small" /> : <Typography variant='h6' >Task Calendar</Typography>}
            </div>
            <Calendar
                value={panelValue}
                onPanelChange={(val) => setPanelValue(val)}
                cellRender={cellRender}
                fullscreen
                headerRender={({ value, type, onChange, onTypeChange }) => {
                    const current = value.clone();

                    return (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '0 16px',
                            }}
                        >
                            <div style={{ display: 'flex', gap: 16, fontSize: 14, fontWeight: 600 }}>
                                <span>
                                    <Badge color="#52c41a" size="default" /> Completed
                                </span>
                                <span>
                                    <Badge color="#faad14" /> In Progress
                                </span>
                                <span>
                                    <Badge color="#1677ff" /> Scheduled
                                </span>
                                <span>
                                    <Badge color="#ff4d4f" /> Cancelled
                                </span>
                            </div>

                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Typography variant="body2" style={{ marginRight: 8 }}>
                                    {current.format('MMMM YYYY')}
                                </Typography>
                                <div>
                                    <Button size='small' variant='outlined' onClick={() => onChange(current.clone().subtract(1, 'month'))}><ArrowLeft /></Button>
                                    <Button size='small' variant='outlined' onClick={() => onChange(current.clone().add(1, 'month'))} style={{ marginLeft: 8 }}>
                                        <ArrowRight />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                }}
            />
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                title={`Tasks on ${selectedDate ? formatYMD(selectedDate) : ''}`}
                footer={null}
                width={720}
            >
                {loadingTasks ? (
                    <Spin />
                ) : (
                    <List
                        dataSource={tasks}
                        locale={{ emptyText: 'No tasks' }}
                        renderItem={(item: any) => (
                            <List.Item>
                                <List.Item.Meta
                                    title={<strong>{item.title} <small style={{ color: '#888' }}>({item.type})</small></strong>}
                                    description={item.description}
                                />
                                <List.Item.Meta
                                    title={<div>
                                        <div><strong>Property:</strong> {item.propertyId?.name || '—'}</div>
                                        <div><strong>Vendor:</strong> {item.vendor?.name || '—'}</div>
                                    </div>}
                                    description={item.notes}
                                />
                                <Space style={{ cursor: 'pointer' }}>
                                    <Tooltip title="Click to update status" placement='top'>
                                    <Tag style={{ textTransform: 'capitalize' }} color={item.status === 'completed' ? 'green' :
                                        item.status === 'in_progress' ? 'gold' :
                                            item.status === 'cancelled' ? 'red' : 'blue'}  onClick={handleStatusPop}>
                                        {statusLabels[item.status]}
                                    </Tag>
                                    </Tooltip>
                                </Space>

                                <Menu id="basic-menu"
                                    anchorEl={statusPop}
                                    open={openStatus}
                                    onClose={handleClose}
                                    slotProps={{
                                        list: {
                                            'aria-labelledby': 'basic-button',
                                        },
                                    }}>
                                    {['scheduled', 'in_progress', 'completed', 'cancelled'].map((status) => (
                                        <MenuItem
                                            key={status}
                                            onClick={async () => {
                                                handleClose();
                                                try {
                                                    await axiosInstance.put(`/api/tasks/${item._id}`, { status });
                                                    toast({
                                                        variant: "success",
                                                        title: "Success!",
                                                        description: "Status updated",
                                                    });
                                                    openDayTasks(selectedDate);
                                                    fetchStatsForMonth(panelValue);
                                                } catch (error) {
                                                    toast({
                                                        variant: "destructive",
                                                        title: "Error!",
                                                        description: "Something went wrong",
                                                    });
                                                }
                                            }}
                                        >
                                            {statusLabels[status]}
                                        </MenuItem>
                                    ))}
                                </Menu>

                            </List.Item>
                        )}
                    />
                )}
            </Modal>
        </>
    );
}
